import { useEffect, useMemo, useState } from "react";
import { Sparkles, Workflow, Network, LayoutGrid, BarChart3, Settings2, Link2, BookOpenCheck } from "lucide-react";
import PipelineTab from "./PipelineTab";
import ClustersTab from "./ClustersTab";
import SeoIntelTab from "./SeoIntelTab";
import PerformanceTab from "./PerformanceTab";
import ArticleWorkspace from "./ArticleWorkspace";
import TopicsLibraryView from "./TopicsLibraryView";
import BriefGeneratorView from "./BriefGeneratorView";
import DraftQueueView from "./DraftQueueView";
import InternalLinkSettings from "./InternalLinkSettings";
import BlogAnalyticsTab from "./BlogAnalyticsTab";
import ErrorBoundary from "@/components/ErrorBoundary";
import { supabase } from "@/integrations/supabase/client";
import { fetchBriefQueue } from "@/lib/contentOps/api";

type StudioTab = "topics" | "briefs" | "drafts";
type OpsTab    = "pipeline" | "clusters" | "seo-intel" | "performance" | "analytics" | "settings";
type AnyTab    = StudioTab | OpsTab;

const OPS_TABS: { id: OpsTab; label: string; Icon: typeof Workflow }[] = [
  { id: "pipeline",    label: "Pipeline",    Icon: Workflow },
  { id: "clusters",    label: "Clusters",    Icon: Network },
  { id: "seo-intel",   label: "SEO Intel",   Icon: LayoutGrid },
  { id: "performance", label: "Performance", Icon: BarChart3 },
  { id: "analytics",   label: "Blog Analytics", Icon: BookOpenCheck },
  { id: "settings",    label: "Settings",    Icon: Link2 },
];

const ContentOperations = () => {
  const [tab, setTab] = useState<AnyTab>("topics");
  const [opsOpen, setOpsOpen] = useState(false);
  const [openArticleId, setOpenArticleId] = useState<string | null>(null);
  const [openArticleTab, setOpenArticleTab] = useState<string>("draft");
  const [topicCount, setTopicCount] = useState(0);
  const [briefCount, setBriefCount] = useState(0);

  const openArticle = (id: string, defaultTab: string = "draft") => {
    setOpenArticleTab(defaultTab);
    setOpenArticleId(id);
  };

  // Allow other admin views (e.g. Blog list) to open an article in the workspace.
  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent).detail as { id?: string; tab?: string } | undefined;
      if (detail?.id) openArticle(detail.id, detail.tab || "draft");
    };
    window.addEventListener("contentops:open-article", onOpen);
    return () => window.removeEventListener("contentops:open-article", onOpen);
  }, []);

  // Live counts for tab labels
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      const [t, b] = await Promise.all([
        supabase.from("content_topics").select("id", { count: "exact", head: true }).neq("status", "rejected"),
        fetchBriefQueue().catch(() => []),
      ]);
      if (cancelled) return;
      setTopicCount(t.count ?? 0);
      setBriefCount(Array.isArray(b) ? b.length : 0);
    };
    refresh();
    const ch = supabase
      .channel("studio-counts")
      .on("postgres_changes", { event: "*", schema: "public", table: "content_topics" }, refresh)
      .on("postgres_changes", { event: "*", schema: "public", table: "content_briefs" }, refresh)
      .subscribe();
    return () => { cancelled = true; supabase.removeChannel(ch); };
  }, []);

  const STUDIO_TABS = useMemo(() => ([
    { id: "topics" as const, label: `Topics Library (${topicCount})` },
    { id: "briefs" as const, label: `Brief Generator` },
    { id: "drafts" as const, label: `Draft Queue (${briefCount})` },
  ]), [topicCount, briefCount]);

  if (openArticleId) {
    return (
      <ErrorBoundary fallbackTitle="Workspace failed to load">
        <ArticleWorkspace articleId={openArticleId} defaultTab={openArticleTab} onClose={() => setOpenArticleId(null)} />
      </ErrorBoundary>
    );
  }

  return (
    <div className="space-y-6">
      {/* Studio header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold flex items-center gap-1.5"
            style={{ color: "hsl(42 55% 65%)" }}>
            <Sparkles size={11} /> Elevate Studio
          </div>
          <h2 className="text-3xl font-bold tracking-tight mt-1" style={{ color: "hsl(0 0% 97%)" }}>
            Content Studio
          </h2>
          <p className="text-sm mt-1" style={{ color: "hsl(0 0% 60%)" }}>
            Editorial topic library, brief generator, and draft review queue.
          </p>
        </div>
        <button
          onClick={() => setOpsOpen((v) => !v)}
          className="self-start sm:self-auto inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border"
          style={{
            background: opsOpen ? "hsl(0 0% 10%)" : "transparent",
            borderColor: "hsl(0 0% 18%)",
            color: opsOpen ? "hsl(0 0% 95%)" : "hsl(0 0% 65%)",
          }}
        >
          <Settings2 size={12} /> Operations {opsOpen ? "▴" : "▾"}
        </button>
      </div>

      {/* Sinai-style 3-tab editorial nav */}
      <div className="flex flex-wrap gap-2">
        {STUDIO_TABS.map((t) => {
          const active = t.id === tab;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className="px-4 py-2 rounded-md text-sm font-medium transition-all border"
              style={{
                background: active ? "hsl(42 55% 30% / 0.20)" : "hsl(0 0% 6%)",
                borderColor: active ? "hsl(42 55% 55%)" : "hsl(0 0% 16%)",
                color: active ? "hsl(42 60% 85%)" : "hsl(0 0% 70%)",
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Operations row (secondary, collapsible) */}
      {opsOpen && (
        <div className="rounded-lg border p-2 flex flex-wrap gap-1"
          style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 14%)" }}>
          <div className="text-[10px] uppercase tracking-[0.18em] w-full px-2 pt-1 pb-1.5"
            style={{ color: "hsl(0 0% 45%)" }}>
            Operations · supporting views
          </div>
          {OPS_TABS.map((t) => {
            const active = t.id === tab;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
                style={{
                  background: active ? "hsl(0 0% 12%)" : "transparent",
                  color: active ? "hsl(0 0% 95%)" : "hsl(0 0% 60%)",
                  border: `1px solid ${active ? "hsl(0 0% 22%)" : "transparent"}`,
                }}
              >
                <t.Icon size={12} />
                {t.label}
              </button>
            );
          })}
        </div>
      )}

      <ErrorBoundary fallbackTitle="This view failed to load">
        {tab === "topics" && <TopicsLibraryView onOpenArticle={(id) => openArticle(id, "brief")} />}
        {tab === "briefs" && <BriefGeneratorView onOpenArticle={(id, defaultTab) => openArticle(id, defaultTab || "draft")} />}
        {tab === "drafts" && <DraftQueueView onOpenArticle={(id) => openArticle(id, "draft")} />}

        {tab === "pipeline"    && <PipelineTab onOpenArticle={(id) => openArticle(id, "draft")} />}
        {tab === "clusters"    && <ClustersTab />}
        {tab === "seo-intel"   && <SeoIntelTab />}
        {tab === "performance" && <PerformanceTab />}
        {tab === "analytics"   && <BlogAnalyticsTab />}
        {tab === "settings"    && <InternalLinkSettings />}
      </ErrorBoundary>
    </div>
  );
};

export default ContentOperations;
